import { useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useApprovedReviews } from "@/hooks/useReviews";
import { ReviewCard } from "./ReviewCard";
import { cn } from "@/lib/utils";

export function ReviewsCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: reviews, isLoading, error } = useApprovedReviews(5);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Don't render if no reviews or error
  if (error || (!isLoading && (!reviews || reviews.length === 0))) {
    return null;
  }

  return (
    <section id="reviews" className="py-16 bg-[var(--color-bg)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-xs font-medium uppercase tracking-widest text-[var(--color-text-muted)] mb-2 block">
            Testimonials
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text)]">
            What Our <em className="not-italic text-[var(--color-primary)]">Clients</em> Say
          </h2>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
          </div>
        )}

        {/* Carousel */}
        {!isLoading && reviews && reviews.length > 0 && (
          <div className="relative">
            {/* Scroll buttons */}
            {reviews.length > 1 && (
              <>
                <button
                  onClick={() => scroll("left")}
                  className={cn(
                    "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10",
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    "bg-[var(--color-bg-card)] border border-[var(--color-border)]",
                    "text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]",
                    "transition-colors shadow-lg",
                    "hidden md:flex"
                  )}
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => scroll("right")}
                  className={cn(
                    "absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10",
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    "bg-[var(--color-bg-card)] border border-[var(--color-border)]",
                    "text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]",
                    "transition-colors shadow-lg",
                    "hidden md:flex"
                  )}
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}

            {/* Cards container */}
            <div
              ref={scrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 -mx-4 px-4"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="flex-shrink-0 w-[300px] sm:w-[320px] snap-start"
                >
                  <ReviewCard review={review} showArtist showImage />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
